import { AssistantConfirmationPrompt } from '@/components/molecules/AssistantChat/AssistantConfirmationPrompt'
import { AssistantInput } from '@/components/molecules/AssistantChat/AssistantInput'
import { AttachButton } from '@/components/molecules/AssistantChat/AttachButton'
import { AttachmentChips } from '@/components/molecules/AssistantChat/AttachmentChips'
import {
  AssistantMessageList,
  type AssistantMessageListHandle,
} from '@/components/molecules/AssistantChat/AssistantMessageList'
import { AssistantRateLimitBanner } from '@/components/molecules/AssistantChat/AssistantRateLimitBanner'
import { chatbotApi } from '@/services/api/domains/chatbot.api'
import { useRef, useState, useSyncExternalStore, type CSSProperties, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'

import {
  answerConfirmation,
  dismissConfirmation,
  getConfirmSnapshot,
  subscribeConfirm,
} from '../../../stores/assistant-confirm-store'
import type { AssistantForm } from '../../../stores/assistant-store'
import { ASSISTANT_LAYER_CLASS } from './assistant-layer'
import { PANEL_ANCHOR_KEY, panelDefaultAnchor, panelSizeFor } from './assistant-placement'
import { AssistantPanelHeader } from './AssistantPanelHeader'
import { useAssistant } from './AssistantProvider'
import { useAttachmentDrop } from './useAttachmentDrop'
import { useRestoreChipsOnFailure } from './useRestoreChipsOnFailure'
import { DockResizeHandle } from './DockResizeHandle'
import { useDockInset, useDockWidth } from './useDockWidth'
import { useEdgeAnchoredDrag } from './useEdgeAnchoredDrag'

/** The floating window's frame. Size and position are inline — see `panelSizeFor`. */
const FLOATING_CLASS = `fixed ${ASSISTANT_LAYER_CLASS} flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-2xl dark:border-gray-700 dark:bg-gray-900`

/** Eases the snap after a drop; off while dragging, so the window tracks the pointer exactly. */
const SETTLE_CLASS = 'transition-[left,top] duration-150 ease-out motion-reduce:transition-none'

/**
 * The dock. Below md it is a full-width overlay under the nav (there is nothing to push on a phone);
 * from md up it is an in-flow column in AppLayout's row beside `<main>`, so the page reflows narrower
 * and both stay usable. Its width arrives through `--assistant-dock-width` — the class below must
 * keep naming that variable literally for Tailwind to generate it.
 */
const DOCK_CLASS = `fixed inset-x-0 bottom-0 top-[65px] ${ASSISTANT_LAYER_CLASS} flex w-full flex-col bg-white shadow-xl dark:bg-gray-900 md:relative md:inset-auto md:z-auto md:w-[var(--assistant-dock-width)] md:shrink-0 md:border-l md:border-gray-200 md:shadow-none md:dark:border-gray-700`

/**
 * The open conversation — header, transcript, the confirmation card, composer.
 *
 * Split out of `AssistantSurface.tsx` rather than shaved down: that file reached
 * the 300-line rule when the Yes/No card landed, and the two halves already
 * divide cleanly. The OUTER shell reads only `assistant-store` (no context, so
 * it cannot throw where no provider is mounted — see the note on
 * `AssistantSurface`); this half consumes `useAssistant()` and only ever mounts
 * once the panel is open.
 */
export function AssistantPanel({ form }: { form: AssistantForm }) {
  const { state, send, sendMessage, attachments } = useAssistant()
  const { t } = useTranslation('console')
  const [input, setInput] = useState('')
  const messageListRef = useRef<AssistantMessageListHandle>(null)
  const confirmation = useSyncExternalStore(subscribeConfirm, getConfirmSnapshot).pending
  const panelDrag = useEdgeAnchoredDrag({
    storageKey: PANEL_ANCHOR_KEY,
    sizeFor: panelSizeFor,
    defaultAnchor: panelDefaultAnchor,
  })
  const dock = form === 'dock'
  const dockWidth = useDockWidth()
  useDockInset(dock ? dockWidth.width : null)

  const sending = state.matches({ active: 'sending' })
  const streaming = state.matches({ active: 'streaming' })
  const active = sending || streaming
  const isRateLimited = state.matches('rateLimited')
  const toolPending = Boolean(state.context.toolResultOutstanding)
  const inputDisabled = active || isRateLimited || toolPending
  const attach = useAttachmentDrop(attachments.addFiles, inputDisabled)
  // A send that fails (a 400, the network, the rate limit) gives its chips back.
  const rememberSent = useRestoreChipsOnFailure(
    { active, failed: isRateLimited || Boolean(state.context.error) },
    attachments.restore,
  )

  const rateLimitResetAt =
    isRateLimited && state.context.error && typeof state.context.error === 'object'
      ? (state.context.error as { resetAt: string | null }).resetAt
      : null
  const formattedResetAt = formatResetAt(rateLimitResetAt)

  const onSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (!input.trim() || active || isRateLimited || toolPending || attachments.blockReason) return
    // Refused here, before anything leaves, when the attachments together are over the size budget.
    const payload = attachments.takeForSend(state.context.activeStill)
    if (!payload) return
    const message = input.trim()
    setInput('')
    rememberSent(attachments.chips)
    attachments.clear()
    // Sending is an explicit "back in the live conversation" action — resume
    // stick-to-bottom following even if the user had scrolled up.
    messageListRef.current?.resumeFollowing()
    sendMessage(message, payload)
  }

  const onClear = () => {
    const { sessionId } = state.context

    // Delete server-side first, but never block the local clear on it.
    // The single most likely reason a user reaches for this button is
    // that the conversation is already in a bad state, so a failing
    // delete must not also deny them the reset.
    if (sessionId) {
      chatbotApi.deleteSession(sessionId).catch(() => {
        /* ignore -- the local clear still proceeds */
      })
    }

    // A pending Yes/No is about to leave the screen with the rest of
    // the conversation. Answer it NO rather than abandoning it — an
    // unanswered client tool suspends the server's run.
    dismissConfirmation()

    // CLEAR (not just a message wipe) also nulls `sessionId`, so the
    // next turn starts a genuinely new session rather than re-posting
    // against the one just deleted.
    send({ type: 'CLEAR' })
  }

  const header = (
    <AssistantPanelHeader
      form={form}
      canClear={state.context.messages.length > 0}
      onClear={onClear}
      onDragStart={panelDrag.onPointerDown}
      dragging={panelDrag.dragging}
    />
  )

  const body = (
    <>
      {isRateLimited && (
        <AssistantRateLimitBanner formattedResetAt={formattedResetAt} onDismiss={() => send({ type: 'CLEAR_ERROR' })} />
      )}
      <AssistantMessageList
        ref={messageListRef}
        messages={state.context.messages}
        streaming={streaming}
        sending={sending}
        currentToolCalls={state.context.currentToolCalls}
        currentResponse={state.context.currentResponse}
        error={state.context.error}
        anon={false}
      />
      {confirmation && (
        <AssistantConfirmationPrompt
          request={confirmation}
          onAnswer={confirmed => answerConfirmation(confirmation.confirmationId, confirmed)}
        />
      )}
      <AssistantInput
        input={input}
        onInputChange={setInput}
        onSubmit={onSubmit}
        placeholder={t('assistant.panel.placeholder', 'Ask a question…')}
        disabled={inputDisabled}
        streaming={streaming || toolPending}
        isRateLimited={isRateLimited}
        onStop={() => {
          dismissConfirmation()
          send({ type: 'CANCEL' })
        }}
        anon={false}
        activeStill={state.context.activeStill}
        onClearStill={() => send({ type: 'CLEAR_STILL' })}
        attachmentsSlot={<AttachmentChips chips={attachments.chips} onRemove={attachments.remove} />}
        attachAction={<AttachButton onFiles={attachments.addFiles} disabled={inputDisabled} />}
        onPaste={attach.onPaste}
        notice={attachments.notice}
        submitBlockedReason={attachments.blockReason}
      />
    </>
  )

  // `<aside>` is the one root for BOTH forms, and both forms render it in the
  // same place (AppLayout's row beside <main>). Toggling bubble <-> dock
  // therefore only diffs this element's props — React never mounts a fresh
  // aside, which would destroy `AssistantMessageList` (re-arming stickToBottom,
  // snapping to the bottom). The separator is slot 0 in the dock and a `false`
  // placeholder in the bubble, so the header and body keep their slots across
  // the toggle too.
  //
  // The floating form is `fixed`, so that place does not move it. It does
  // decide who wins a z-index tie, though: coming after <main>, the window
  // would win every tie with a page or header overlay. That is why its layer
  // sits between the overlay tiers (`assistant-layer.ts`).
  //
  // No backdrop: the dock takes room from the page instead of covering it, and
  // the page must stay usable while the assistant drives it. ✕ closes.
  //
  // `data-assistant-panel` marks BOTH forms for page-level key handlers that
  // must leave the assistant's keys alone. The dock is no longer a dialog, so
  // `closest('[role="dialog"]')` stopped finding it (see the Forms builder's
  // `escapeBelongsToTarget`).
  return (
    <aside
      data-assistant-panel=""
      role={dock ? undefined : 'dialog'}
      aria-label={t('assistant.panel.title', 'Assistant')}
      data-testid={dock ? 'assistant-dock' : 'assistant-bubble'}
      style={
        dock
          ? ({ '--assistant-dock-width': `${dockWidth.width}px` } as CSSProperties)
          : {
              left: panelDrag.point.x,
              top: panelDrag.point.y,
              width: panelDrag.size.width,
              height: panelDrag.size.height,
            }
      }
      className={`${dock ? DOCK_CLASS : `${FLOATING_CLASS} ${panelDrag.dragging ? '' : SETTLE_CLASS}`} ${
        attach.dragging ? 'ring-2 ring-inset ring-primary-500' : ''
      }`}
      {...attach.dropHandlers}
    >
      {dock && <DockResizeHandle {...dockWidth} />}
      {header}
      {body}
    </aside>
  )
}

function formatResetAt(resetAt: string | null): string {
  if (!resetAt) return 'midnight UTC'

  const date = new Date(resetAt)

  if (Number.isNaN(date.getTime())) return 'midnight UTC'

  return date.toLocaleString()
}
